import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const RelocationList = ({ records = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v ?? "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Relocation Records", 14, 10);

        const tableColumn = [
            "#",
            "Date",
            "Patient Name",
            "Gender",
            "DOB",
            "Contact",
            "Department",
            "Standard Name",
        ];

        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.issueDate || r.date || "",
            r.patientName || "",
            r.patientGender || "",
            r.patientDOB || "",
            r.patientContact || "",
            r.departmentName || "",
            r.standardName || "",
        ]);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("relocation_records.pdf");
    };

    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() =>
                            onChangePage(Math.min(totalPages, currentPage + 1), rowCount)
                        }
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        { name: "Standard Name", selector: (r) => r.standardName, sortable: true },
        { name: "Department", selector: (r) => r.departmentName, sortable: true },
        { name: "Patient Name", selector: (r) => r.patientName, sortable: true },
        { name: "Gender", selector: (r) => r.patientGender, sortable: true },
        { name: "DOB", selector: (r) => r.patientDOB, sortable: true },
        { name: "Contact", selector: (r) => r.patientContact, sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEdit && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => onEdit(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => onDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "130px",
        },
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="relocation_records.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search Relocation Records..."
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    <button className="btn btn-sm btn-primary" onClick={onAdd}>
                        <i className="ri-add-line me-1"></i> Add New
                    </button>
                </div>
            </div>

            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={loading}
                    noDataComponent={<div>No records found</div>}
                />
            </div>
        </div>
    );
};

export default RelocationList;
